import { LitElement, html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import Styles from './vm-video-hub-card.scss';
import { trackHubComponentImpression, trackHubViewClick } from 'Analytics/video-detail-events';
import 'Components/vm-button/vm-button';
import 'Components/vm-profile-image/vm-profile-image';
import 'Components/vm-card/vm-card';

/**
 * A presentational "hub"-style card: logo, title, and a CTA, with an optional `description`
 * slot for body content (e.g. a related-content list). The card is agnostic about what it
 * points at — channel hubs, playlists, webinars — so callers supply the wording via the
 * required `card-cta-label` ("Visit Hub" for the original channel-hub use).
 *
 * The whole header links to `cardUrl`, which is passed to vm-card's `href`, so vm-card wraps
 * its body in a full-cover link (keyboard-focusable, open-in-new-tab). The CTA button is
 * therefore decorative — the overlay carries the navigation — and the `description` sits
 * outside the overlay so links inside it stay clickable. `cardCtaLabel` doubles as that
 * overlay link's accessible name, since the anchor has no visible text of its own.
 *
 * `card-cta-label` is typed as required, but types don't reach callers that set it from an
 * empty Django context variable. When it comes through empty the button is dropped rather
 * than rendered blank, and `cardTitle` names the overlay link so it never goes unlabelled.
 *
 * This component is breakpoint-agnostic — it always renders the card chrome. Callers
 * that need responsive behaviour (card on desktop, bare content on mobile) should use
 * `vm-video-hub-related`, which composes this component.
 *
 * @slot description - Optional. Body content rendered below the card header.
 */
@customElement('vm-video-hub-card')
export class VmVideoHubCard extends LitElement {
    static override styles = Styles;

    @property({ type: String, attribute: 'card-title' }) cardTitle = '';
    @property({ type: String, attribute: 'card-image-src' }) cardImageSrc = '';
    @property({ type: String, attribute: 'card-image-alt' }) cardImageAlt = '';
    @property({ type: String, attribute: 'card-cta-label' }) cardCtaLabel!: string;
    @property({ type: String, attribute: 'card-url' }) cardUrl = '';
    /** The qualifying channel's slug, reported as `channel_id` on the hub Mixpanel events. */
    @property({ type: String, attribute: 'channel-slug' }) channelSlug = '';

    private _impressionObserver?: IntersectionObserver;

    override connectedCallback() {
        super.connectedCallback();

        // Fire HubComponentImpression when the card scrolls into view. The mobile and desktop
        // hubs are separate components, but trackHubComponentImpression is deduped per page, so
        // only whichever card the user actually sees emits the event.
        this._impressionObserver = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (!entry.isIntersecting) return;
                this._impressionObserver?.disconnect();
                if (!this.channelSlug) return;
                trackHubComponentImpression(this.channelSlug);
            });
        });
        this._impressionObserver.observe(this);

        this.addEventListener('click', this._onClick);
    }

    override disconnectedCallback() {
        super.disconnectedCallback();
        this._impressionObserver?.disconnect();
        this.removeEventListener('click', this._onClick);
    }

    // The whole card header navigates via vm-card's overlay anchor (href === cardUrl). Body content
    // slotted into `description` (e.g. related-video cards on desktop) links elsewhere, so match the
    // overlay's href to fire HubViewClick only for the hub navigation.
    private _onClick = (event: MouseEvent) => {
        if (!this.channelSlug) return;
        const navigatedToHub = event
            .composedPath()
            .some((el) => el instanceof HTMLAnchorElement && el.getAttribute('href') === this.cardUrl);

        if (navigatedToHub) trackHubViewClick(this.channelSlug);
    };

    override render() {
        // Only forward the description slot when there's slotted body content. Otherwise
        // vm-card would treat the forwarded <slot> as a description child and render its
        // empty bordered/padded region below the header (visible on the body-less mobile card).
        const hasBody = !!this.querySelector(':scope > [slot="description"]');

        return html`
            <vm-card href=${this.cardUrl} link-label=${this.cardCtaLabel || this.cardTitle}>
                ${this.cardImageSrc
                    ? html`<vm-profile-image
                          slot="avatar"
                          class="vm-video-hub-card__logo"
                          imageSrc=${this.cardImageSrc}
                          imageAlt=${this.cardImageAlt || this.cardTitle}
                          fit="contain"
                      ></vm-profile-image>`
                    : nothing}
                <span slot="text" class="vm-video-hub-card__title">${this.cardTitle}</span>
                ${this.cardCtaLabel
                    ? html`<vm-button
                          variant="ghost"
                          color-scheme="neutral"
                          size="sm"
                          slot="cta"
                          class="vm-video-hub-card__cta"
                          >${this.cardCtaLabel}</vm-button
                      >`
                    : nothing}
                ${hasBody ? html`<slot name="description" slot="description"></slot>` : nothing}
            </vm-card>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'vm-video-hub-card': VmVideoHubCard;
    }
}
